import { EyeOutlined } from '@ant-design/icons';
import { useTheme } from '@material-ui/core';
import { getRecharges } from '@portal/actions/device';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedModalFilters from '@portal/components/AdvancedModalFilters/AdvancedModalFilters';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import DataTable from '@portal/components/DataTable/DataTable';
import FormattedBilletStatement from '@portal/components/FormattedBilletStatement/FormattedBilletStatement';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import {
  RechargeCategoryEnum,
  RechargeStatusEnum,
} from '@portal/enum/recharge';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { AdvancedFilterType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { maskPhone, unmaskField } from '@portal/services/masks';
import { queryStringToObject } from '@portal/services/queryString';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';
import { Tag, Tooltip } from 'antd';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

const initialValues: advancedFilterModels.RechargeMobileFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
};

const RechargeHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const { palette } = useTheme();
  const {
    device: { rechargeHistory },
  } = useReduxState();

  const { search } = useLocation();

  const [advancedFilters, setAdvancedFilters] = useState(initialValues);
  const [openModal, setOpenModal] = useState(false);
  const [receiptData, setReceiptData] = useState<models.RechargeReceipt | null>(
    null
  );
  const filterParams = new URLSearchParams(search);

  const onSearch = (filters: advancedFilterModels.RechargeMobileFilter) => {
    const queryParams = queryStringToObject();
    const newFilter = {
      ...queryParams,
      ...filters,
      pageSize: filters.pageSize ?? 10,
      phoneNumber: filters.phoneNumber
        ? unmaskField(filters.phoneNumber)
        : undefined,
    };

    setAdvancedFilters(newFilter);
    dispatch(getRecharges(newFilter));
  };

  const handleStatusOptions = useMemo(() => {
    const statusOption: models.SelectOptions[] = [];

    for (const status in RechargeStatusEnum) {
      const parsedStatus = parseInt(status, 10);

      if (!isNaN(parsedStatus)) {
        statusOption.push({
          name: getEnumDescription(parsedStatus, 'RECHARGE_STATUS'),
          value: parsedStatus,
        });
      }
    }

    return statusOption;
  }, []);

  const handleOpenDetails = (receipt: models.RechargeReceipt) => {
    setReceiptData(receipt);
    setOpenModal(true);
  };

  const formatReceiptText = (text: string): string => {
    return text
      .replace(/<VIA1>/g, '')
      .replace(/<\/VIA1>/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
  };

  const renderReceipt = () => {
    if (!receiptData) {
      return (
        <div className="recharge-selection__receipt">
          <ThemedText>
            {translate('PAGES.RECHARGE.HISTORY.DETAIL_MODAL.NO_RECEIPT')}
          </ThemedText>
        </div>
      );
    }

    return (
      <div className="recharge-selection__receipt">
        {receiptData.receiptformatted ? (
          <FormattedBilletStatement
            billet={formatReceiptText(receiptData.receiptformatted)}
          />
        ) : null}
      </div>
    );
  };

  const closeModal = () => {
    setOpenModal(false);
    setReceiptData(null);
  };

  const handleCategory = (category: RechargeCategoryEnum) => {
    let color = '';

    switch (category) {
      case RechargeCategoryEnum.GAMES:
        color = palette.secondary.main;
        break;
      case RechargeCategoryEnum.TRANSPORT:
        color = palette.error.main;
        break;
      case RechargeCategoryEnum.CELLPHONE:
        color = palette.warning.main;
        break;
      case RechargeCategoryEnum.TV:
        color = palette.info.main;
        break;
      case RechargeCategoryEnum.DIGITAL_CONTENT:
        color = palette.success.main;
        break;

      default:
        color = palette.grey[500];
        break;
    }

    return (
      <Tag color={color} className="recharge-selection__tag">
        {getEnumDescription(category, 'RECHARGE_CATEGORY')}
      </Tag>
    );
  };

  const handleStatus = (status: RechargeStatusEnum) => {
    let color = '';

    switch (status) {
      case RechargeStatusEnum.PROCESSING:
        color = palette.warning.main;
        break;
      case RechargeStatusEnum.DONE:
        color = palette.success.main;
        break;
      case RechargeStatusEnum.ERROR:
        color = palette.error.main;
        break;

      default:
        color = palette.grey[500];
        break;
    }

    return (
      <Tag color={color} className="recharge-selection__tag">
        {getEnumDescription(status, 'RECHARGE_STATUS')}
      </Tag>
    );
  };

  const handleCategoryEnum = useMemo(() => {
    const statusOption: models.SelectOptions[] = [];

    for (const status in RechargeCategoryEnum) {
      const parsedStatus = parseInt(status, 10);

      if (!isNaN(parsedStatus)) {
        statusOption.push({
          name: getEnumDescription(parsedStatus, 'RECHARGE_CATEGORY'),
          value: parsedStatus,
        });
      }
    }

    return statusOption.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.RECHARGE.SELECTION.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            title: translate('PAGES.RECHARGE.SELECTION.TITLE'),
            active: false,
            url: `${getRouteStackPath('RECHARGE', 'RECHARGE_SELECTION_PAGE')}`,
          },
          {
            title: translate('PAGES.RECHARGE.MODE.HISTORY.TITLE'),
            active: true,
          },
        ]}
      />
      <div className="recharge-selection">
        <AdvancedPanelContentHeader
          testId="recharge-history-header"
          title={translate('PAGES.RECHARGE.MODE.HISTORY.TITLE')}
          description={translate('PAGES.RECHARGE.MODE.HISTORY.DESCRIPTION')}
          buttonTitle={translate('APPLICATION.BACK')}
          goBackAction={() =>
            navigate(getRouteStackPath('RECHARGE', 'RECHARGE_SELECTION_PAGE'))
          }
        />
        <AdvancedModalFilters
          fields={[
            {
              name: 'category',
              placeholder: translate(
                'PAGES.RECHARGE.HISTORY.ADVANCED_FILTERS.CATEGORY'
              ),
              type: AdvancedFilterType.SELECT,
              defaultValue: filterParams.get('category') ?? '',
              options: handleCategoryEnum,
            },

            {
              name: 'status',
              placeholder: translate(
                'PAGES.RECHARGE.HISTORY.ADVANCED_FILTERS.STATUS'
              ),
              type: AdvancedFilterType.SELECT,
              options: handleStatusOptions,
              defaultValue: filterParams.get('status') ?? '',
            },
            {
              name: 'createdAt',
              placeholder: [
                translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_PAGE.ADVANCED_FILTER.CREATED_AT.START'
                ),
                translate(
                  'PAGES.EMPLOYEES.EMPLOYEES_PAGE.ADVANCED_FILTER.CREATED_AT.END'
                ),
              ],
              type: AdvancedFilterType.RANGE_PICKER,
              defaultValue: filterParams.get('createdAt') ?? '',
              disableFuture: true,
              onlyDate: true,
            },
          ]}
          onSearch={onSearch}
        />
        <AdvancedCard testId="recharge-history-table">
          <DataTable
            rows={rechargeHistory?.data ?? []}
            columns={[
              {
                field: 'id',
                flex: 1,
                headerName: 'id',
                sortable: false,
                hide: true,
              },
              {
                field: 'signerCode',
                flex: 1,
                headerName: translate(
                  'PAGES.RECHARGE.HISTORY.COLUMNS.SIGNER_CODE'
                ),
                renderCell: ({ row }) => <>{row?.signerCode}</>,
              },
              {
                field: 'topupValue',
                flex: 1,
                headerName: translate('PAGES.RECHARGE.HISTORY.COLUMNS.AMOUNT'),
                renderCell: ({ row }) =>
                  row?.topupValue && <>{moneyFormat(row.topupValue)}</>,
              },
              {
                field: 'category',
                flex: 1,
                headerName: translate(
                  'PAGES.RECHARGE.HISTORY.COLUMNS.CATEGORY'
                ),
                renderCell: ({ row }) => handleCategory(row?.category),
              },
              {
                field: 'phoneNumber',
                flex: 1,
                headerName: translate(
                  'PAGES.RECHARGE.HISTORY.COLUMNS.PHONE_NUMBER'
                ),
                hide: true,
                renderCell: ({ row }) => <>{maskPhone(row?.phoneNumber)}</>,
              },
              {
                field: 'status',
                flex: 1,
                headerName: translate('PAGES.RECHARGE.HISTORY.COLUMNS.STATUS'),
                renderCell: ({ row }) => <>{handleStatus(row?.status)}</>,
              },
              {
                field: 'createdAt',
                flex: 1,
                headerName: translate(
                  'PAGES.RECHARGE.HISTORY.COLUMNS.CREATED_AT'
                ),
                renderCell: ({ row }) => {
                  if (!row?.createdAt) return <>-</>;
                  const date = DateTime.fromISO(row.createdAt);
                  return (
                    <>
                      {date.isValid
                        ? date.toLocaleString(DateTime.DATETIME_SHORT)
                        : '-'}
                    </>
                  );
                },
              },
              {
                field: 'action',
                align: 'center',
                headerAlign: 'center',
                headerName: translate(
                  'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.ACTIONS'
                ),
                flex: 1,
                renderCell: ({ row }) => (
                  <Tooltip
                    title={translate(
                      'PAGES.PAYMENT.ACCOUNT_PAYABLE.COLUMNS.DETAILS'
                    )}
                    className="account-payable__details"
                  >
                    <button
                      className="transparent-button users__details--button"
                      type="button"
                      onClick={() => handleOpenDetails(row?.responsePayload)}
                    >
                      <EyeOutlined />
                    </button>
                  </Tooltip>
                ),
              },
            ]}
            rowCount={rechargeHistory?.total}
            pageSize={advancedFilters?.pageSize ?? 1}
            page={advancedFilters?.page ?? 1}
            sort={advancedFilters?.sort ?? 'desc'}
            orderBy={advancedFilters?.orderBy ?? 'createdAt'}
            onChange={(filters) => {
              const searchFilters = {
                ...advancedFilters,
                ...filters,
              };
              setAdvancedFilters(searchFilters);
              onSearch(searchFilters);
            }}
          />
        </AdvancedCard>
        <GenericModal
          visible={openModal}
          onClose={() => closeModal()}
          title={translate('PAGES.RECHARGE.HISTORY.DETAIL_MODAL.TITLE')}
          size="small"
        >
          {renderReceipt()}
        </GenericModal>
      </div>
    </>
  );
};

export default RechargeHistory;
